import {VFC, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {EzCard, EzCardExpandable, EzHeading, EzCardSection} from '@ezcater/recipe';
import {parseISO, format} from 'date-fns';
import type {DriverProgressAssignmentQuery} from 'services/graphql/types';
import CardHeader from './CardHeader';
import MapLink from './MapLink';
import ContactSection from '../InProgress/ContactSection';

type Assignment = NonNullable<
  NonNullable<DriverProgressAssignmentQuery['me']>['driverAccount']
>['assignment'];

type DeliveryProps =
  | {
      type: 'pickup';
      address: Assignment['pickupAddress'] | null;
      deliverySetup?: never;
      distanceInMiles?: never;
      durationInMinutes?: never;
      dropoffWindow?: never;
      eventAt?: never;
    }
  | {
      type: 'dropoff';
      address: Assignment['dropoffAddress'] | null;
      eventAt: string;
      deliverySetup: Assignment['deliverySetup'];
      distanceInMiles: Assignment['distanceInMiles'];
      dropoffWindow: Assignment['dropoffWindow'];
      durationInMinutes: Assignment['durationInMinutes'];
    };

type Props = {
  isMapLinkDisabled?: boolean;
  driverAskFor?: string | null;
} & DeliveryProps;

const eventAtFontStyle = {fontWeight: 800};
const deliveryWindowFontStyle = {fontWeight: 500};

const formatDate = (value: string) => format(parseISO(value), 'EEEE LLL d, p');
const formatWindowDate = (value: string) => format(parseISO(value), 'p');

const DeliveryAddressCard: VFC<Props> = ({
  type,
  address,
  isMapLinkDisabled = true,
  driverAskFor,
  eventAt,
  deliverySetup,
  distanceInMiles,
  dropoffWindow,
  durationInMinutes,
}) => {
  const {t} = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const toggle = () => setIsExpanded(expanded => !expanded);

  const labelType = type === 'pickup' ? 'Pickup' : 'Dropoff';

  const expandable = address?.instructions
    ? {
        isExpanded,
        onClick: toggle,
        expandLabel: t(`driverProgress.deliveryAddressCard.expandable.expand${labelType}Label`),
        collapseLabel: t(`driverProgress.deliveryAddressCard.expandable.collapse${labelType}Label`),
      }
    : undefined;

  return (
    <EzCard expandable={expandable}>
      <EzCardSection>
        <div>
          <CardHeader
            type={type}
            distanceInMiles={distanceInMiles}
            durationInMinutes={durationInMinutes}
          />
          {!!eventAt && (
            <EzHeading style={eventAtFontStyle} size="5">
              {formatDate(eventAt)}
            </EzHeading>
          )}
          {dropoffWindow && (
            <EzHeading style={deliveryWindowFontStyle} size="5">
              ({t('driverProgress.deliveryAddressCard.deliveryWindow')}:{' '}
              {formatWindowDate(dropoffWindow.startAt)} - {formatWindowDate(dropoffWindow.endAt)})
            </EzHeading>
          )}
          <EzHeading color="blue" size="2">
            {address?.locationName ?? ''}
          </EzHeading>
          <MapLink disabled={isMapLinkDisabled} address={address} />
        </div>
        {expandable && (
          <EzCardExpandable isExpanded={isExpanded}>
            <div style={{marginTop: '1rem'}} />
            <ContactSection
              deliverySetup={deliverySetup}
              driverAskFor={driverAskFor}
              instructions={address?.instructions}
              type={type}
            />
          </EzCardExpandable>
        )}
      </EzCardSection>
    </EzCard>
  );
};

export default DeliveryAddressCard;
